import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import DocumentTopicSchema, { DocumentTopicJSON, DocumentTopicParams } from "documents/topics/_schema/document-topic-schema";
import ValueObject from "_framework/entities/values/value-object";

export default class DocumentTopic implements ValueObject<DocumentTopic, DocumentTopicJSON, DocumentTopicParams> {
  public static get EXAMPLE(): DocumentTopic {
    return new DocumentTopic({
      entityClass: "ExampleClass",
      entityId: Guid.generate(),
      context: "ExampleContext",
    });
  }

  public static create(values: DocumentTopicParams): DocumentTopic {
    const parsed = DocumentTopicSchema.validate(values);
    return new DocumentTopic(parsed);
  }

  public static fromJSON(json: DocumentTopicJSON): DocumentTopic {
    const parsed = DocumentTopicSchema.deserialize(json);
    return new DocumentTopic(parsed);
  }

  public static fromJSONArray(
    json: (DocumentTopicJSON | null | undefined)[] | null | undefined,
    options?: { filterNullValues: boolean }
  ): DocumentTopic[] | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const opts = options ?? { filterNullValues: false };
    const topics: DocumentTopic[] = [];
    for (const topicJSON of json) {
      if (isNullOrUndefined(topicJSON)) continue;
      const topic = DocumentTopic.fromJSON(topicJSON);
      if (!topic && !opts.filterNullValues)
        throw new InvalidDocumentTopicJSONError({
          values: topicJSON,
          message: "Invalid Document Topic",
        });
      if (topic) topics.push(topic);
    }
    return topics;
  }

  public readonly entityClass?: string;
  public readonly entityId?: Guid;
  public readonly context?: string;

  private constructor(params: DocumentTopicParams) {
    this.entityClass = params.entityClass;
    this.entityId = params.entityId;
    this.context = params.context;
  }


  public toJSON(): DocumentTopicJSON {
    return DocumentTopicSchema.serialize(this);
  }
  
  public isEqualTo(other: DocumentTopic | null | undefined): boolean {
    return Boolean(
      ((!this.entityClass && !other?.entityId) ||
        this.entityId?.isEqualTo(other?.entityId)) &&
      ((!this.context && !other?.context) || this.context === other?.context)
    );
  }

  public toString(): string {
    return (
      (this.entityClass ? "Class: " + this.entityClass : "") +
      (this.entityId ? " Id: " + this.entityId.value : "") +
      (this.context ? " Context: " + this.context : "")
    );
  }
}

export class InvalidDocumentTopicJSONError extends Error {
  constructor(context: {
    values: DocumentTopicJSON | null | undefined;
    message: string;
  }) {
    super(`Invalid Document Topic JSON: ${context.message}`);
    this.name = "InvalidDocumentTopicJSONError";
  }
}

export class InvalidDocumentTopicError extends Error {
  constructor(context: { values: Partial<DocumentTopic>; message: string }) {
    super(`Invalid Document Topic: ${context.message}`);
    this.name = "InvalidDocumentTopicError";
  }
}
